import { GoogleMap, GoogleMapProps, OverlayView } from '@react-google-maps/api';
import { GridAlgorithm, MarkerClusterer } from '@googlemaps/markerclusterer';
import { IEquipmentDetails } from '../../../../libraries/equipment';
import { ILocation } from '../../../../libraries/location';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from '../../../client-redux/reducers';
import { Language } from '../../../languages/languageHandler';
import { Paper, Title, Text, Group } from '@mantine/core';
import zarphInitial from '../../../../assets/img/zarph_Z_logo.svg';
import './styles.scss';

const containerStyle = {
  width: '100%',
  height: '400px'
};

function getColor(cap: any) {
  if (cap >= 95) return 'red';
  if (cap >= 80) return 'orange';
  return 'green';
}

function getMapCenter(pins: MapPin[]): { lat: number; lng: number } {
  const validPositions = pins.map(p => p.position).filter(pos => pos && !isNaN(pos.lat) && !isNaN(pos.lng));
  if (validPositions.length === 0) {
    return { lat: 38.73, lng: -9.14 };
  }
  const latSum = validPositions.reduce((sum, pos) => (pos ? sum + pos.lat : sum), 0);
  const lngSum = validPositions.reduce((sum, pos) => (pos ? sum + pos.lng : sum), 0);
  return {
    lat: latSum / validPositions.length,
    lng: lngSum / validPositions.length
  };
}

export interface MapPin {
  location: ILocation;
  equipments: IEquipmentDetails[];
  position?: google.maps.LatLngLiteral;
}

interface MapMarkersProps {
  pins: MapPin[];
}

function MapMarkers({ pins }: MapMarkersProps) {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const [hoveredPin, setHoveredPin] = useState<MapPin | null>(null);
  const [mapCenter] = useState(getMapCenter(pins));
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);

  const Tooltipscale = 0.8;

  useEffect(() => {
    if (!mapInstance || pins.length === 0) return;

    let clusterer: MarkerClusterer | null = null;
    let markers: google.maps.marker.AdvancedMarkerElement[] = [];
    let timeoutId: NodeJS.Timeout;

    const createClusters = async () => {
      const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;

      markers = pins.map(pin => {
        const maxOcc = Math.max(...pin.equipments.map(e => e.safeOccPerc || 0));
        const statusColors = pin.equipments.map(e => getColor(e.safeOccPerc || 0));
        const errorCount = statusColors.filter(c => c !== 'green').length;
        const badgeColor = errorCount > 0 ? getColor(maxOcc) : null;

        const markerContent = document.createElement('div');
        markerContent.innerHTML = `
  <svg width="35" height="35" viewBox="0 0 35 35" xmlns="http://www.w3.org/2000/svg">
    <circle cx="17.5" cy="17.5" r="17.5" fill="rgba(255, 255, 255, 0.8)"/>
    <image href="${zarphInitial}" x="6.5" y="6.5" width="22" height="22" preserveAspectRatio="xMidYMid meet"/>
    ${
      badgeColor
        ? `<circle cx="28" cy="28" r="8" fill="${badgeColor}" stroke="white" stroke-width="2"/> 
           <text x="28" y="31" text-anchor="middle" font-size="10" font-weight="bold" fill="white">${errorCount}</text>`
        : ''
    }
  </svg>
`;

        const marker = new AdvancedMarkerElement({
          position: pin.position || mapCenter,
          title: pin.location.name,
          content: markerContent
        });

        marker.addListener('click', () => {
          setHoveredPin(hoveredPin?.location.sid === pin.location.sid ? null : pin);
        });

        return marker;
      });

      timeoutId = setTimeout(() => {
        clusterer = new MarkerClusterer({
          map: mapInstance,
          algorithm: new GridAlgorithm({ gridSize: 60, maxZoom: 15 })
        });

        try {
          clusterer.addMarkers(markers);
        } catch (err) {}
      }, 500);
    };

    createClusters();

    return () => {
      clearTimeout(timeoutId);
      if (clusterer && clusterer.getMap()) {
        clusterer.clearMarkers();
      }
      markers.forEach(m => (m.map = null));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapInstance, pins]);

  if (!pins || pins.length === 0) return null;

  const mapOptions: GoogleMapProps['options'] = {
    disableDefaultUI: true,
    zoomControl: true,
    mapId: 'DEMO_MAP_ID',
    clickableIcons: false
  };

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={mapCenter}
      zoom={13}
      options={mapOptions}
      onLoad={map => setMapInstance(map)}
      onClick={() => setHoveredPin(null)}
    >
      {hoveredPin && hoveredPin.position && (
        <OverlayView
          position={hoveredPin.position}
          mapPaneName={OverlayView.FLOAT_PANE}
          getPixelPositionOffset={() => ({
            x: 5 / Tooltipscale,
            y: -5 / Tooltipscale
          })}
        >
          <div className="map-marker-tooltip" onWheel={e => e.stopPropagation()}>
            <Title order={4}>{hoveredPin.location.name}</Title>

            {hoveredPin.equipments.map(equi => (
              <Paper key={equi.equipment.eid} className="equipment-entry">
                <Group className="location-equipment">
                  <Title>{`${lang.GLOBAL_EQUIPMENT}: `}</Title>
                  <Text>{equi.equipment.eid}</Text>
                </Group>
                <Group className="location-equipment">
                  <Title order={5}>{`${lang.GLOBAL_CAPACITY}: `}</Title>
                  <Text>{equi.safeOccPerc || 0} %</Text>
                </Group>
              </Paper>
            ))}
          </div>
        </OverlayView>
      )}
    </GoogleMap>
  );
}

export default MapMarkers;
